import {
  Alignment,
  Button,
  Icon,
  InputGroup,
  Intent,
  Menu,
  MenuItem,
  Navbar,
  Popover,
  Position,
  Text
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useOktaAuth} from "@okta/okta-react";
import axios from "axios";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {createAvatarComponent, GoogleSource, GravatarSource, IconSource, ValueSource} from "react-avatar";
import Scrollbars from "react-custom-scrollbars-2";
import {Link, useHistory, useLocation} from "react-router-dom";
import log from "loglevel";
import {useAppContext} from "../../ApplicationContext";
import {env} from "../../config";
import {useGetDispatchStationRestrictionInfoLazyQuery, useSetUserPrefMutation} from "../../generated/graphql";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  usePreferenceContext
} from "../../providers/PreferenceProvider";
import {switchTenants, Tenant, useTenantService} from "../../services/TenantService";
import {AppToaster} from "../../utils/toasters/toaster";
import {useAuthState} from "../../providers/AuthProvider";
import {COLORS, Constants, TenantPreferences, UserPreferences} from "../../constants/constants";
import FilterableMultiSelect from "../common/FilterableMultiSelect";
import {DispatchStation} from "../common/types";
import {OptionType} from "../common/types/option";
import useInterval, {IntervalFunction} from "../../hooks/useInterval";
import {useAssignmentDispatch} from "../../providers/AssignmentProdiver";
import {
  DispatchStationActionTypes,
  useDispatchStationContext,
  useDispatchStationDispatch
} from "../../providers/DispatchStationProvider";
import DispatchMessaging from "../messaging/DispatchMessaging";
import {useManifestDetailsDispatch, useManifestDetailsState} from "../../providers/ManifestDetailsProvider";
import {useMapVisibilityDispatch} from "../map/MapVisibilityContext";

type AppProps = {
  authLogout(): Promise<void>;
  authLogin(): Promise<void>;
  refresh(): void;
  mainLoading(loading: boolean): void;
  resetCache(): Promise<unknown>;
};

const CustomAvatar = createAvatarComponent({
  sources: [GravatarSource, ValueSource, IconSource, GoogleSource]
});

const determineLoadedVersion = async (): Promise<string> => {
  const versionRegex = /src="\/static\/js\/main\.(.*)\.js"/;
  return axios
    .get("/")
    .then(async (response) => response.data)
    .catch((reason) => {
      log.warn("Unable to determine version.", reason);
      return "";
    })
    .then((htmlText) => {
      const regexResult = versionRegex.exec(htmlText);
      if (regexResult && regexResult.length >= 2) {
        log.debug(`Returning loaded version ${regexResult[1]}`);
        return regexResult[1];
      } else {
        log.debug("no loaded version found.  Localhost?");
        return "";
      }
    });
};

const getDispatchStationOptions = (dispatchStations: Map<string, DispatchStation>, sort = true): OptionType[] => {
  const options = Array.from(dispatchStations.values()).map((ds) => {
    return {
      key: ds.stationId,
      value: ds.description
    } as OptionType;
  });

  if (sort) {
    options.sort((a, b) => a.value.localeCompare(b.value));
  }

  return options;
};

const DispatchNavBar = ({authLogout, refresh, mainLoading, resetCache}: AppProps) => {
  const history = useHistory();
  const location = useLocation();
  const {oktaAuth} = useOktaAuth();
  const {user, tenantId, isAdmin, isSuperUser, tenants, isEcourierUser, setActiveTenant} = useAuthState();
  const mapVisibilityDispatch = useMapVisibilityDispatch();
  const {error, loading, data} = useTenantService({url: "/list"});
  const [tenantFilter, setTenantFilter] = useState<string>();
  const {
    driverMessaging: messagingEnabled,
    globalFind: globalFindEnabled,
    routePlanning: routePlanningEnabled
  } = useFeatureFlagContext();

  const {selectedDispatchStations, defaultDispatchStation} = useDispatchStationContext();
  const dispatchStationDispatch = useDispatchStationDispatch();

  const {appState, dispatch} = useAppContext();
  const {selectedDriverChannelId, selectedDriverUnreadCount, totalUnreadChannels} = appState;
  const assignmentStateDispatch = useAssignmentDispatch();
  const manifestDetailsState = useManifestDetailsState();
  const manifestDetailsDispatch = useManifestDetailsDispatch();

  const [isInitialized, setIsInitialized] = useState(false);
  const [initialVersion, setInitialVersion] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");
  const [updatePrompt, setUpdatePrompt] = useState(false);
  const [dispatchStationQuery, setDispatchStationQuery] = useState<string>("");
  const {dispatchStations: allDispatchStations, isDispatchDataLoaded} = useDispatchDataContext();
  const [setUserPref] = useSetUserPrefMutation();
  const {userPreferences, isUserPreferencesLoaded, tenantPreferences, userPrefsQueryRefetch} = usePreferenceContext();

  const isRestrictedByCompanySetting = useMemo(() => {
    return extractSimplePref(tenantPreferences, TenantPreferences.restrictStationVisibility, false).value;
  }, [tenantPreferences]);

  const [fetchDispatchStationRestriction, {loading: restrictLoading}] = useGetDispatchStationRestrictionInfoLazyQuery(
    {}
  );

  const dispatchStations = useMemo(() => {
    const map = new Map<string, DispatchStation>();
    allDispatchStations.forEach((ds) => map.set(ds.stationId, ds));
    return map;
  }, [allDispatchStations]);

  const filteredDispatchStations = useMemo(() => {
    let result = allDispatchStations;

    if (defaultDispatchStation && defaultDispatchStation.length > 0) {
      const stationIds = new Set(defaultDispatchStation.map((ds) => ds.stationId));
      result = result.filter((ds) => stationIds.has(ds.stationId));
    }

    if (dispatchStationQuery?.trim()) {
      const tokens = dispatchStationQuery.trim().toLowerCase().split(" ");
      result = result.filter((ds) => {
        return tokens.every(
          (token) => ds.description.toLowerCase().includes(token) || ds.stationId.toLowerCase().includes(token)
        );
      });
    }

    return result;
  }, [allDispatchStations, dispatchStationQuery, defaultDispatchStation]);

  const dispatchStationOptions = useMemo(() => {
    return getDispatchStationOptions(new Map(filteredDispatchStations.map((ds) => [ds.stationId, ds])));
  }, [filteredDispatchStations]);

  // Initialize selected dispatch stations from user preferences
  useEffect(() => {
    if (!isInitialized && isUserPreferencesLoaded && isDispatchDataLoaded && dispatchStations.size > 0) {
      const selectedStations = extractJsonPref(userPreferences, UserPreferences.dispatchStationsGroup, []).value;

      const resolvedDispatchStations = selectedStations.reduce((acc: DispatchStation[], dispatchStation: string) => {
        if (dispatchStations.has(dispatchStation)) {
          acc.push(dispatchStations.get(dispatchStation) as DispatchStation);
        }
        return acc;
      }, [] as DispatchStation[]);

      dispatchStationDispatch({
        type: DispatchStationActionTypes.INITIAL_SELECTED_DISPATCH_STATIONS,
        payload: resolvedDispatchStations
      });
      setIsInitialized(true);
    }
  }, [
    dispatchStationDispatch,
    dispatchStations,
    isInitialized,
    isUserPreferencesLoaded,
    isDispatchDataLoaded,
    userPreferences
  ]);

  useEffect(() => {
    const fetchRestrictions = async () => {
      if (!isInitialized || !isUserPreferencesLoaded || !isDispatchDataLoaded) {
        return;
      }
      if (isRestrictedByCompanySetting && !isEcourierUser && !isSuperUser) {
        const data = await fetchDispatchStationRestriction();
        const restrictData = data.data?.getDispatchStationRestrictionInfo;
        if (restrictData?.dispatchStation !== "0" && restrictData?.dispatchGridFlag === 0) {
          const restrictedStationIds = new Set([...restrictData.subDispatchStations!, restrictData.dispatchStation]);

          const restrictedStations = allDispatchStations.filter((ds) => restrictedStationIds.has(ds.stationId));

          if (restrictedStations.length === 0) {
            log.warn(`No dispatch station group found for restricted station ${restrictData?.dispatchStation}`);
            return;
          }

          if (restrictedStations.length > 0) {
            dispatchStationDispatch({
              type: DispatchStationActionTypes.SET_DEFAULT_DISPATCH_STATIONS,
              payload: restrictedStations
            });

            const selectedStations = extractJsonPref(userPreferences, UserPreferences.dispatchStationsGroup, []).value;
            const restrictedStationsSelected = restrictedStations.filter((station) =>
              selectedStations.includes(station.stationId)
            );

            if (restrictedStationsSelected.length > 0) {
              dispatchStationDispatch({
                type: DispatchStationActionTypes.INITIAL_SELECTED_DISPATCH_STATIONS,
                payload: restrictedStationsSelected
              });
            } else {
              dispatchStationDispatch({
                type: DispatchStationActionTypes.CLEAR_SELECTED_DISPATCH_STATIONS
              });
            }
            await setUserPref({
              variables: {
                name: UserPreferences.dispatchStationsGroup,
                input: createJsonPref(
                  restrictedStationsSelected.map((s) => s.stationId),
                  true
                )
              }
            });
            userPrefsQueryRefetch?.();
          }
        } else {
          dispatchStationDispatch({type: DispatchStationActionTypes.CLEAR_DEFAULT_DISPATCH_STATION});
        }
      } else {
        dispatchStationDispatch({type: DispatchStationActionTypes.CLEAR_DEFAULT_DISPATCH_STATION});
      }
      dispatchStationDispatch({type: DispatchStationActionTypes.SET_IS_DISPATCH_STATION_LOADED, payload: true});
    };
    (async () => {
      await fetchRestrictions();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatchStationDispatch,
    fetchDispatchStationRestriction,
    isEcourierUser,
    isInitialized,
    isRestrictedByCompanySetting,
    isSuperUser,
    isUserPreferencesLoaded,
    isDispatchDataLoaded,
    allDispatchStations,
    setUserPref,
    userPrefsQueryRefetch
  ]);

  const onDispatchStationSelected = (option: OptionType) => {
    if (selectedDispatchStations.has(option.key as string)) {
      selectedDispatchStations.delete(option.key as string);
      const dispatchStation = dispatchStations.get(option.key as string);
      if (dispatchStation) {
        log.debug("De-selected Dispatch Station: ", dispatchStation);
        dispatchStationDispatch({
          type: DispatchStationActionTypes.REMOVE_SELECTED_DISPATCH_STATION,
          payload: dispatchStation
        });
      }
    } else {
      const newSelectedDispatchStations = dispatchStations.get(option.key as string);
      if (!newSelectedDispatchStations) {
        log.warn(`Attempted to select Dispatch Station ${option.key} that does not exist.`);
        return;
      }
      selectedDispatchStations.set(option.key as string, newSelectedDispatchStations);
      log.debug("Selected Dispatch Station: ", newSelectedDispatchStations);
      dispatchStationDispatch({
        type: DispatchStationActionTypes.ADD_SELECTED_DISPATCH_STATION,
        payload: newSelectedDispatchStations
      });
    }
    setUserPref({
      variables: {
        name: UserPreferences.dispatchStationsGroup,
        input: createJsonPref(Array.from(selectedDispatchStations.keys()), true)
      }
    })
      .then(() => userPrefsQueryRefetch?.())
      .catch((error) => {
        log.warn(`Error saving user preference ${error.message}`, error);
      });

    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  };

  const onDispatchStationsCleared = () => {
    dispatchStationDispatch({type: DispatchStationActionTypes.CLEAR_SELECTED_DISPATCH_STATIONS});
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
    setUserPref({
      variables: {
        name: UserPreferences.dispatchStationsGroup,
        input: createJsonPref([], true)
      }
    }).catch((error) => {
      log.warn(`Error saving user preference ${error.message}`, error);
    });
  };

  const updateVersion: IntervalFunction = () => {
    determineLoadedVersion()
      .then((updatedVersion) => {
        setCurrentVersion(updatedVersion);
      })
      .catch((error) => {
        log.warn(`Unable to determine loaded version ${error.message}`, error);
      });
  };

  useInterval({callback: updateVersion, delay: env.updateCheckInSeconds * 1000});

  const handleNav = useCallback(
    (
      location: string,
      panelsOpenState?: {assignedStops?: boolean; manifestDetails?: boolean; globalFind?: boolean; chat?: boolean}
    ) => {
      dispatch({type: "SetIsGlobalFindOpen", payload: panelsOpenState?.globalFind ?? false});
      dispatch({type: "SetIsChatOpen", payload: panelsOpenState?.chat ?? false});

      history.push(location);
    },
    [dispatch, history]
  );

  const openMessaging = () => {
    dispatch({type: "SetIsChatOpen", payload: true});
  };

  const onOpenGlobalFind = () => {
    handleNav("/jobs", {globalFind: true});
  };

  const handleLogout = useCallback(() => {
    authLogout().catch((error) => {
      log.warn(`Error logging out with authentication provider ${error.message}`, error);
    });
  }, [authLogout]);

  const handleTenantSwitch = (tenant: Tenant) => {
    log.log(`Switching to tenant: ${JSON.stringify(tenant)}`);
    mainLoading(true);
    switchTenants(oktaAuth.getAccessToken() ?? "", tenant.id)
      .then(() => oktaAuth.tokenManager.renew("accessToken"))
      .then(() => {
        setActiveTenant?.(tenant.id, tenant);
        return resetCache();
      })
      .then(() => {
        AppToaster.show({
          intent: Intent.SUCCESS,
          icon: "tick",
          message: `Switched to tenant: ${tenant.shortName}`
        });
        refresh();
        mainLoading(false);
      })
      .catch((e) => {
        AppToaster.show({
          intent: Intent.WARNING,
          icon: "warning-sign",
          message: `Error Switching Tenants: ${e}`
        });
        log.error("Error switching tenants", e);
        mainLoading(false);
      });
  };

  const getAllowedTenants = useCallback(() => {
    if (!loading && !error) {
      if (isSuperUser) {
        return data;
      } else {
        return data?.filter((x) => tenants?.includes(x.shortName));
      }
    } else {
      return [] as Tenant[];
    }
  }, [data, error, isSuperUser, loading, tenants]);

  useEffect(() => {
    determineLoadedVersion()
      .then((version) => {
        setInitialVersion(version);
        setCurrentVersion(version);
      })
      .catch((error) => {
        log.warn(`Unable to determine loaded version ${error.message}`, error);
      });
  }, []);

  useEffect(() => {
    if (!updatePrompt && initialVersion && currentVersion && initialVersion !== currentVersion) {
      setUpdatePrompt(true);
      log.debug(`Version ${initialVersion} is different than ${currentVersion}`);
      AppToaster.show({
        action: {
          text: "Update Now",
          onClick: () => window.location.reload()
        },
        intent: Intent.PRIMARY,
        message:
          "A new version of Dispatch is available.  Click 'Update Now' to update or update any time by clicking your browser's refresh button.",
        onDismiss: () => {
          setUpdatePrompt(false);
        },
        timeout: 0
      });
    }
  }, [updatePrompt, initialVersion, currentVersion]);

  useEffect(() => {
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
    assignmentStateDispatch({type: "ClearSelections"});
    manifestDetailsDispatch({type: "CLEAR_MANIFEST_DETAILS"});
  }, [location.pathname, assignmentStateDispatch, manifestDetailsDispatch, mapVisibilityDispatch]);

  const renderTenantMenuItem = (tenant: Tenant) => {
    return (
      <MenuItem
        data-testid={`tenant-menu-item-${tenant.id}`}
        key={`tenant-menu-item-${tenant.id}`}
        text={tenant.shortName}
        onClick={() => handleTenantSwitch(tenant)}
        intent={tenant.id === tenantId ? Intent.PRIMARY : Intent.NONE}
        icon={tenant.id === tenantId ? "tick" : "blank"}
      />
    );
  };

  const renderTenantSelection = () => {
    const allowedTenants = getAllowedTenants();
    if (allowedTenants?.length === 0 || (allowedTenants?.length === 1 && allowedTenants[0].id === tenantId)) {
      return null;
    } else if (allowedTenants) {
      const currentTenant = allowedTenants.find((x) => x.id === tenantId);
      return (
        <MenuItem data-testid="tenant-switch-menu" icon="briefcase" text="Customer">
          <InputGroup
            autoFocus
            aria-autocomplete="list"
            leftIcon="search"
            placeholder="Filter..."
            onChange={(event) => setTenantFilter((event.target as HTMLInputElement).value)}
            value={tenantFilter}
          />
          <Scrollbars autoHeight autoHeightMax="50vh">
            {currentTenant && renderTenantMenuItem(currentTenant)}
            {allowedTenants
              .filter((x) => (tenantFilter ? x.shortName.toLowerCase().indexOf(tenantFilter.toLowerCase()) >= 0 : true))
              .filter((x) => x.id !== tenantId)
              .sort((x, y) => (x.shortName < y.shortName ? -1 : 1))
              .map((tenant) => {
                return renderTenantMenuItem(tenant);
              })}
          </Scrollbars>
        </MenuItem>
      );
    }
  };

  const unreadMessage = useMemo(() => {
    if (selectedDriverChannelId) {
      return selectedDriverUnreadCount;
    }
    return totalUnreadChannels <= Constants.MAX_UNREAD_MESSAGE_COUNT
      ? totalUnreadChannels
      : Constants.MAX_UNREAD_MESSAGE_COUNT + "+";
  }, [selectedDriverChannelId, selectedDriverUnreadCount, totalUnreadChannels]);

  const menu = (
    <Menu style={{borderRadius: "6px"}}>
      <MenuItem icon="info-sign" text="User Info" onClick={() => handleNav("/whoami")} />
      {renderTenantSelection()}
      {isAdmin && (
        <MenuItem
          data-testid={"settings-menu-item"}
          icon={<FontAwesomeIcon icon={solid("gear")} />}
          text={"Settings"}
          href={"/settings"}
        />
      )}
      <MenuItem icon="log-out" text="Logout" onClick={handleLogout} />
    </Menu>
  );

  return (
    <Fragment>
      {oktaAuth.getAccessToken() && (
        <>
          <Navbar data-testid={"navbar-root"}>
            <Navbar.Group align={Alignment.START}>
              <Fragment>
                <Navbar.Heading>
                  <FontAwesomeIcon style={{paddingRight: "8px"}} color={COLORS.TEXT_GRAY} icon={solid("headset")} />
                  <span>Dispatch</span>
                </Navbar.Heading>
                <Navbar.Divider />
              </Fragment>
              <span></span>
              <Link to={"/jobs"}>
                <NavbarButton
                  variant="minimal"
                  icon={<Icon icon="home" color={location.pathname === "/jobs" ? COLORS.BLUE : undefined} />}
                  text={
                    <Text
                      style={{
                        color: location.pathname === "/jobs" ? COLORS.BLUE : undefined
                      }}
                    >
                      Home
                    </Text>
                  }
                />
              </Link>
              <Link to={"/assigned-stops"}>
                <NavbarButton
                  variant="minimal"
                  icon={
                    <Icon
                      icon="list"
                      size={14}
                      color={location.pathname === "/assigned-stops" ? COLORS.BLUE : undefined}
                    />
                  }
                  text={
                    <Text
                      style={{
                        color: location.pathname === "/assigned-stops" ? COLORS.BLUE : undefined
                      }}
                    >
                      Assigned Stops
                    </Text>
                  }
                />
              </Link>
              {routePlanningEnabled && (
                <Link to={"/itinerary-planning"}>
                  <NavbarButton
                    variant="minimal"
                    icon={
                      <Icon
                        icon="automatic-updates"
                        size={14}
                        color={location.pathname === "/itinerary-planning" ? COLORS.BLUE : undefined}
                      />
                    }
                    text={
                      <Text
                        style={{
                          color: location.pathname === "/itinerary-planning" ? COLORS.BLUE : undefined
                        }}
                      >
                        Itinerary Planning
                      </Text>
                    }
                  />
                </Link>
              )}
              {messagingEnabled && (
                <NavbarButton
                  variant="minimal"
                  icon={<MessagingIcon />}
                  text="Messaging"
                  onClick={openMessaging}
                  style={{position: "relative"}}
                >
                  {unreadMessage !== 0 && <MessageNumber>{unreadMessage}</MessageNumber>}
                </NavbarButton>
              )}
            </Navbar.Group>

            <Navbar.Group align={Alignment.END}>
              {globalFindEnabled && (
                <>
                  <GlobalFindButton
                    onClick={onOpenGlobalFind}
                    icon={<Icon color={COLORS.TEXT_GRAY} icon="search" size={14} />}
                    variant="minimal"
                    text="Global Find"
                  />
                  <Navbar.Divider />
                </>
              )}
              {
                <FilterableMultiSelect
                  options={dispatchStationOptions}
                  testIdPrefix="dispatch-station"
                  selectedOptions={getDispatchStationOptions(selectedDispatchStations, false)}
                  text={`Dispatch Stations${
                    selectedDispatchStations.size > 0 ? ` (${selectedDispatchStations.size})` : ""
                  }`}
                  placeholder={"Search Dispatch Stations"}
                  onOptionSelect={onDispatchStationSelected}
                  onClear={onDispatchStationsCleared}
                  onQueryChange={setDispatchStationQuery}
                  activeBackGroundColor="#14305A"
                  defaultTextColor="#161616"
                  iconSize={14}
                  maxHeight={360}
                  loading={restrictLoading}
                  hideSearch={Boolean(defaultDispatchStation) && defaultDispatchStation!.length <= 1}
                />
              }
              <Navbar.Divider />
              <Popover content={menu} position={Position.BOTTOM_RIGHT} portalClassName="settings-portal">
                <NavbarButton data-testid={"user-avatar-button"} className="bp5-minimal">
                  <CustomAvatar name={user?.name} email={user?.email} round={true} size="36" textSizeRatio={2} />
                </NavbarButton>
              </Popover>
            </Navbar.Group>
          </Navbar>
          <>{messagingEnabled && <DispatchMessaging driverId={manifestDetailsState.driverId} />}</>
        </>
      )}
    </Fragment>
  );
};

export default DispatchNavBar;

const NavbarButton = styled(Button)``;

export const MessageNumber = styled.div`
  color: white;
  position: absolute;
  left: 15%;
  top: -18%;
  min-width: 18px;
  padding: 2px 6px;
  background: #32a467;
  border: 1px solid #ffffff;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 500;
  font-family: "Roboto", sans-serif;
`;

const MessagingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_227_18775)">
      <path
        d="M2.5 0C1.12109 0 0 1.12109 0 2.5V13.75C0 15.1289 1.12109 16.25 2.5 16.25H6.25V19.375C6.25 19.6133 6.38281 19.8281 6.59375 19.9336C6.80469 20.0391 7.05859 20.0156 7.25 19.875L12.082 16.25H17.5C18.8789 16.25 20 15.1289 20 13.75V2.5C20 1.12109 18.8789 0 17.5 0H2.5Z"
        fill="#5F6B7C"
      />
    </g>
    <defs>
      <clipPath id="clip0_227_18775">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const GlobalFindButton = styled(Button)`
  width: 150px;
  border-radius: 4px;
  box-shadow: 0px 1px 2px 0px #00000040 !important;
  background: #ffffff !important;
  justify-content: start;

  .bp5-button-text {
    color: ${COLORS.TEXT_GRAY};
  }
`;
